import React from "react";
import { useDispatch } from "react-redux";
import { checkIsValidPreviewOrigin } from "repoV2/utils/previewPages";
import { ICheckoutListingPage } from "../CheckoutListingPage/ICheckoutListingPage.interfaces";
import CheckoutListingPageBase from "../CheckoutListingPageBase/CheckoutListingPageBase";
import { ICheckoutPagePreviewData } from "./CheckoutListingPageWithPreview.interfaces";
import { CHECKOUT_PAGES_ACTION_TYPES } from "../../constants/CheckoutPagesReducer";
import { checkout_preview_keys } from "./CheckoutListingPageWithPreview.constants";

const CheckoutListingPageWithPreview = (props: ICheckoutListingPage.IProps) => {
    const {
        hostName,
        listingSlug,
        listingDetails,
        creatorDetails,
        merchandiseDetails,
        initBooking,
        thankyouUuid,
        renewSubscription,
        communityUuid,
        dynamicLinkUuid,
        isPreview,
    } = props;

    const [checkoutPageDetails, setCheckoutPageDetails] = React.useState<{
        listingDetails: ICheckoutListingPage.IProps["listingDetails"];
        creatorDetails: ICheckoutListingPage.IProps["creatorDetails"];
    }>({
        listingDetails,
        creatorDetails,
    });

    const dispatch = useDispatch();

    React.useEffect(() => {
        const listenFn = (e: MessageEvent<any>) => {
            if (!checkIsValidPreviewOrigin(e.origin)) return;

            const newListingDetails: ICheckoutListingPage.IProps["listingDetails"] =
                {};
            const newCreatorDetails: ICheckoutListingPage.IProps["creatorDetails"] =
                {};
            const checkoutPagePreviewData: ICheckoutPagePreviewData =
                e?.data || {};

            if (
                checkout_preview_keys.listing_details in checkoutPagePreviewData
            ) {
                const checkoutListingDetails =
                    checkoutPagePreviewData.listingDetails || {
                        listingTitle: "",
                        creatorImage: "",
                        creatorName: "",
                    };
                if (
                    checkout_preview_keys.listing_title in
                    checkoutListingDetails
                ) {
                    newListingDetails.title =
                        checkoutListingDetails.listingTitle;
                }
                if (
                    checkout_preview_keys.creator_image in
                    checkoutListingDetails
                ) {
                    newCreatorDetails.display_image =
                        checkoutListingDetails?.creatorImage;
                }
                if (
                    checkout_preview_keys.creator_name in checkoutListingDetails
                ) {
                    newCreatorDetails.subtitle =
                        checkoutListingDetails.creatorName;
                }
            }

            if (
                checkout_preview_keys.description_section in
                checkoutPagePreviewData
            ) {
                const checkoutDescriptionSection =
                    checkoutPagePreviewData.descriptionSection || {};
                if (
                    checkout_preview_keys.description in
                    checkoutDescriptionSection
                ) {
                    newListingDetails.description =
                        checkoutPagePreviewData.descriptionSection?.description;
                }
            }

            if (
                checkout_preview_keys.cover_section in checkoutPagePreviewData
            ) {
                const checkoutCoverSection =
                    checkoutPagePreviewData.coverSection || {};
                if (checkout_preview_keys.cover_image in checkoutCoverSection) {
                    newListingDetails.cover_image =
                        checkoutPagePreviewData.coverSection?.coverImage;
                }
            }

            if (
                checkout_preview_keys.button_text_section in
                checkoutPagePreviewData
            ) {
                const checkoutButtonSection: ICheckoutPagePreviewData["buttonTextSection"] =
                    checkoutPagePreviewData.buttonTextSection || {
                        buttonType: 1,
                        ctaData: {
                            mobileButtonText: "",
                            paymentButtonText: "",
                        },
                    };
                const currentButtonDetails = listingDetails.cta_details || {};
                if (
                    checkout_preview_keys.button_type in checkoutButtonSection
                ) {
                    newListingDetails.cta_details = {
                        ...currentButtonDetails,
                        button_type: checkoutButtonSection.buttonType || 1,
                    };
                }
                if (checkout_preview_keys.cta_data in checkoutButtonSection) {
                    newListingDetails.cta_details = {
                        button_type:
                            newListingDetails.cta_details?.button_type ||
                            checkoutButtonSection.buttonType,
                        cta_data: {
                            mobile_button_text:
                                checkoutButtonSection.ctaData?.mobileButtonText,
                            payment_button_text:
                                checkoutButtonSection.ctaData
                                    ?.paymentButtonText,
                        },
                    };
                }
            }

            if (
                checkout_preview_keys.color_palette in checkoutPagePreviewData
            ) {
                const checkoutColorPalette =
                    checkoutPagePreviewData.colorPalette || {};
                const existingPaymentLinkDetails =
                    newCreatorDetails.payment_link_appearance || {};
                const existingColorPalette =
                    newCreatorDetails.payment_link_appearance?.color_pallete ||
                    {};
                newCreatorDetails.payment_link_appearance = {
                    ...existingPaymentLinkDetails,
                    color_pallete: {
                        ...existingColorPalette,
                        ...checkoutColorPalette,
                    },
                };
            }

            if (checkout_preview_keys.tab_assets in checkoutPagePreviewData) {
                const checkoutTabAssets =
                    checkoutPagePreviewData.tabAssets || {};
                const existingPaymentLinkDetails =
                    checkoutPageDetails.creatorDetails
                        .payment_link_appearance || {};
                const existingTabAssetsDetails =
                    checkoutPageDetails.creatorDetails.payment_link_appearance
                        ?.tab_assets || {};
                if (checkout_preview_keys.tab_title in checkoutTabAssets) {
                    newCreatorDetails.payment_link_appearance = {
                        ...existingPaymentLinkDetails,
                        tab_assets: {
                            ...existingTabAssetsDetails,
                            tab_title: checkoutTabAssets.tabTitle || "",
                        },
                    };
                }
                if (checkout_preview_keys.tab_icon in checkoutTabAssets) {
                    newCreatorDetails.payment_link_appearance = {
                        ...existingPaymentLinkDetails,
                        tab_assets: {
                            ...existingTabAssetsDetails,
                            tab_icon: checkoutTabAssets.tabIcon || "",
                        },
                    };
                }
            }
            if (
                checkout_preview_keys.order_bumps_heading_section in
                checkoutPagePreviewData
            ) {
                const checkoutOrderBumpsHeadingDetails =
                    checkoutPagePreviewData.orderBumpsHeadingSection || {};
                const currentListingMetaData = listingDetails.metadata || {};
                if (
                    checkout_preview_keys.heading in
                    checkoutOrderBumpsHeadingDetails
                ) {
                    newListingDetails.metadata = {
                        ...currentListingMetaData,
                        custom_order_bump_heading:
                            checkoutOrderBumpsHeadingDetails.heading || "",
                    };
                }
            }
            if (
                checkout_preview_keys.order_bumps_details in
                checkoutPagePreviewData
            ) {
                const checkoutOrderBumpsDetails =
                    checkoutPagePreviewData.orderBumpsDetails || {};
                const newOrderBumpsDetails = {
                    ...checkoutOrderBumpsDetails,
                };
                dispatch({
                    type: CHECKOUT_PAGES_ACTION_TYPES.SET_ORDER_BUMPS_DATA,
                    payload: {
                        listingUuid: listingDetails.uuid,
                        data: {
                            ...newOrderBumpsDetails,
                        },
                    },
                });
            }

            setCheckoutPageDetails(prev => ({
                listingDetails: {
                    ...prev.listingDetails,
                    ...newListingDetails,
                },
                creatorDetails: {
                    ...prev.creatorDetails,
                    ...newCreatorDetails,
                },
            }));
        };

        window.addEventListener("message", listenFn);

        return () => {
            window.removeEventListener("message", listenFn);
        };
    }, [checkoutPageDetails]);

    return (
        <CheckoutListingPageBase
            hostName={hostName}
            listingSlug={listingSlug}
            listingDetails={checkoutPageDetails.listingDetails}
            creatorDetails={checkoutPageDetails.creatorDetails}
            merchandiseDetails={merchandiseDetails}
            initBooking={initBooking}
            thankyouUuid={thankyouUuid}
            renewSubscription={renewSubscription}
            communityUuid={communityUuid}
            dynamicLinkUuid={dynamicLinkUuid}
            isPreview={isPreview}
        />
    );
};

export default CheckoutListingPageWithPreview;
